import React, { useState } from 'react';
import { PreferenceCategory } from '../../types/index';
import { 
  FaUtensils, 
  FaGamepad, 
  FaChair, 
  FaBaseballBall, 
  FaLeaf, 
  FaCouch, 
  FaCompactDisc, 
  FaTshirt, 
  FaHome, 
  FaCar, 
  FaGift, 
  FaMusic, 
  FaTools, 
  FaGem, 
  FaBox, 
  FaStar 
} from 'react-icons/fa';

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: PreferenceCategory[]) => void;
  initialPreferences?: PreferenceCategory[];
}

const preferenceOptions: { value: PreferenceCategory; label: string; icon: React.ReactNode }[] = [
  { value: 'silverware', label: 'Silverware & Cutlery', icon: <FaUtensils className="w-6 h-6" /> },
  { value: 'pokemon-cards', label: 'Pokemon Cards', icon: <FaGamepad className="w-6 h-6" /> },
  { value: 'antiques', label: 'Antiques', icon: <FaChair className="w-6 h-6" /> },
  { value: 'sports-cards', label: 'Sports Cards', icon: <FaBaseballBall className="w-6 h-6" /> },
  { value: 'plants', label: 'Plants', icon: <FaLeaf className="w-6 h-6" /> },
  { value: 'furniture', label: 'Furniture', icon: <FaCouch className="w-6 h-6" /> },
  { value: 'records', label: 'Records', icon: <FaCompactDisc className="w-6 h-6" /> },
  { value: 'clothes', label: 'Clothes', icon: <FaTshirt className="w-6 h-6" /> },
  { value: 'estate-sales', label: 'Estate Sales', icon: <FaHome className="w-6 h-6" /> },
  { value: 'vehicles', label: 'Vehicles', icon: <FaCar className="w-6 h-6" /> },
  { value: 'free-stuff', label: 'Free Stuff', icon: <FaGift className="w-6 h-6" /> },
  { value: 'musical-instruments', label: 'Musical Instruments', icon: <FaMusic className="w-6 h-6" /> },
  { value: 'restoration', label: 'Restoration & Refurbishing', icon: <FaTools className="w-6 h-6" /> },
  { value: 'jewelry', label: 'Jewelry', icon: <FaGem className="w-6 h-6" /> },
  { value: 'collectibles', label: 'Collectibles', icon: <FaBox className="w-6 h-6" /> },
  { value: 'knickknacks', label: 'Knickknacks', icon: <FaStar className="w-6 h-6" /> },
];

export const PreferencesModal: React.FC<PreferencesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialPreferences = [],
}) => {
  const [selectedPreferences, setSelectedPreferences] = useState<PreferenceCategory[]>(initialPreferences);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleTogglePreference = (preference: PreferenceCategory) => {
    setSelectedPreferences(prev =>
      prev.includes(preference)
        ? prev.filter(p => p !== preference)
        : [...prev, preference]
    );
  };

  const handleSave = () => {
    onSave(selectedPreferences);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">What are you interested in finding?</h2>
        <p className="text-gray-600 mb-6">Select the items you're most interested in collecting or finding.</p>
        
        <div className="grid grid-cols-4 md:grid-cols-6 gap-4 mb-6">
          {preferenceOptions.map(option => (
            <div key={option.value} className="relative">
              <button
                onClick={() => handleTogglePreference(option.value)}
                onMouseEnter={() => setHoveredItem(option.value)}
                onMouseLeave={() => setHoveredItem(null)}
                className={`w-full aspect-square flex items-center justify-center rounded-lg border transition-colors ${
                  selectedPreferences.includes(option.value)
                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                {option.icon}
              </button>
              {hoveredItem === option.value && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap">
                  {option.label}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Skip for now
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesModal; 